import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import Button from '@components/atoms/button';
import { BodyRegular, H1, H4 } from '@components/atoms/typography';
import { updateProfileFields } from '@api-service/profile';
import type { UpdateProfileFields } from '@api-service/profile';
import { fetchEntryLeagues } from '@api-service/fpl-entries';
import { MAX_FPL_TEAM_ID } from '@lib/constants';
import {
  DEADLINE_DISPLAY_FORMAT,
  resolveChangeDeadlineAt
} from '@lib/mini-league-promise';
import { useAccessToken } from '@hooks/useAccessToken';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useProfile, USERS_ME_QUERY_KEY } from '@hooks/useProfile';

dayjs.extend(utc);

const SUBMITTED_DATE_FORMAT = 'D MMMM YYYY';

const DIGITS_ONLY = /^\d+$/;

const toPositiveId = (value: string, max = Number.MAX_SAFE_INTEGER) => {
  if (!DIGITS_ONLY.test(value.trim())) return null;
  const parsed = parseInt(value.trim(), 10);
  return parsed > 0 && parsed <= max ? parsed : null;
};

const INPUT_CLASSES =
  'w-full rounded-lg border border-neutral-300 px-4 py-3 font-open-sans text-base text-neutral-900 focus:border-primary-500 focus:outline-none';

const LABEL_CLASSES =
  'mb-1 block font-open-sans text-sm font-bold text-neutral-800';

/**
 * The stored Mini-League Promise nomination on the account page (FFH-2565).
 * View plus change only: the section renders solely when a mini league is
 * already stored, because the post-checkout capture on /funnel-welcome is the
 * one place we reliably know the member qualifies for the offer. First-time
 * capture deliberately does not exist here; it needs a "new paid subscriber
 * this season" signal the client cannot derive (profile createdAt only dates
 * the backend migration, not the signup).
 *
 * The change form covers the FPL ID as well as the league (FFH-2585):
 * members who captured their promise against a mistyped FPL ID need both
 * correctable. A corrected ID must be re-searched before saving, mirroring
 * the funnel capture's drift guard, so a league chosen for one entry can
 * never be saved against another.
 *
 * Changes lock at the gameweek 3 deadline per the amended offer terms
 * (originally gameweek 1; extended under FFH-2672 for members whose league
 * outgrew eligibility or who were blocked around the original cut-off); the
 * stored value stays visible, read-only, for the rest of the season. The
 * lock is UI-only, mirroring FFH-1597: the API stays permissive. While FPL
 * is in maintenance the name lookup is skipped and the stored league renders
 * by ID (FFH-2623).
 */
const MiniLeagueSection: FC = () => {
  const { profile } = useProfile();
  const { accessToken } = useAccessToken();
  const { context } = useLeagueContext();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [fplIdInput, setFplIdInput] = useState('');
  const [lookupFplId, setLookupFplId] = useState<number | null>(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [manualLeagueInput, setManualLeagueInput] = useState('');

  const savedFplTeamId = profile?.fplTeamId ?? null;
  const savedMiniLeagueId = profile?.miniLeagueId ?? null;

  const isFplInMaintenance = context?.isFplUpdating === true;

  // Discard any previously selected/entered league whenever the FPL ID being
  // looked up changes, so a re-lookup for a different team can never carry
  // over a selection that belonged to the old one.
  useEffect(() => {
    setSelectedLeagueId('');
    setManualLeagueInput('');
  }, [lookupFplId]);

  // Resolves the stored league's name for the read-only row. Shares a cache
  // entry with the edit form's lookup whenever that is searching the saved
  // FPL ID (same query key), so opening the form usually refetches nothing.
  // Skipped while FPL is in maintenance (FFH-2623): the lookup can only 502
  // then, so the row falls back to the ID (the same presentation as a failed
  // lookup) instead. useLeagueContext polls every 30s while isFplUpdating is
  // set, so enabling flips back and the name resolves once maintenance ends.
  const storedLeaguesQuery = useQuery({
    queryKey: ['fpl-entry-leagues', savedFplTeamId],
    queryFn: () => fetchEntryLeagues(savedFplTeamId as number),
    enabled:
      !isFplInMaintenance &&
      savedFplTeamId !== null &&
      savedMiniLeagueId !== null,
    retry: false,
    refetchOnWindowFocus: false
  });

  // The edit form's league list, keyed on whichever FPL ID was last
  // searched (the saved one on entry, a corrected one after a re-search).
  const lookupQuery = useQuery({
    queryKey: ['fpl-entry-leagues', lookupFplId],
    queryFn: () => fetchEntryLeagues(lookupFplId as number),
    enabled: isEditing && lookupFplId !== null,
    retry: false,
    refetchOnWindowFocus: false
  });

  const mutation = useMutation({
    mutationFn: async (fields: UpdateProfileFields) => {
      // accessToken is fetched independently of profile and can still be
      // null here; the save button gates on it, but guard again so a stale
      // click never sends an unauthenticated PUT.
      if (!accessToken) throw new Error('No access token available');

      const result = await updateProfileFields(accessToken, fields);
      if (!result.ok) throw new Error(result.message);
      return result.profile;
    },
    onSuccess: () => {
      // useProfile keys on [USERS_ME_QUERY_KEY, accessToken]; invalidating by
      // the prefix refreshes whichever token-scoped entry it is reading.
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
    },
    // Success or failure, the row comes back; a failure reports beside the
    // change button rather than trapping the member in the form.
    onSettled: () => {
      setIsEditing(false);
    }
  });

  if (savedMiniLeagueId === null) return null;

  const changeDeadlineAt = resolveChangeDeadlineAt(context);
  const isChangeOpen =
    changeDeadlineAt !== null && Date.now() < Date.parse(changeDeadlineAt);
  const deadlineDisplay =
    changeDeadlineAt !== null
      ? dayjs(changeDeadlineAt).format(DEADLINE_DISPLAY_FORMAT)
      : null;

  const storedLeagues = storedLeaguesQuery.data ?? [];
  const storedLeagueName =
    storedLeagues.find((league) => league.leagueId === savedMiniLeagueId)
      ?.name ?? null;
  const submittedOn = profile?.miniLeagueCapturedAt
    ? dayjs.utc(profile.miniLeagueCapturedAt).format(SUBMITTED_DATE_FORMAT)
    : null;

  // The input is the source of truth while editing, so a corrected FPL ID
  // (not the saved one) is what gets looked up and saved.
  const effectiveFplTeamId = toPositiveId(fplIdInput, MAX_FPL_TEAM_ID);
  // True once the FPL ID that was actually looked up (whose leagues/fallback
  // are on screen) still matches the current input. False the moment the
  // input drifts away from it (edited after a lookup, before re-searching);
  // without this, save could pair the new FPL ID with a league selected for
  // the old one.
  const isLookupCurrentForInput =
    lookupFplId !== null && lookupFplId === effectiveFplTeamId;
  const canLookUpLeagues =
    effectiveFplTeamId !== null && lookupFplId !== effectiveFplTeamId;

  const editLeagues = lookupQuery.data ?? [];
  const showLeagueFallback =
    lookupQuery.isError || (lookupQuery.isSuccess && editLeagues.length === 0);

  // Pre-select the stored league without an effect: until the member picks,
  // the select shows the stored value whenever the lookup that produced the
  // list was for the saved FPL ID and listed it. A lookup for a corrected ID
  // never inherits the old selection.
  const isStoredLeagueListed =
    lookupFplId === savedFplTeamId &&
    editLeagues.some((league) => league.leagueId === savedMiniLeagueId);
  const selectValue =
    selectedLeagueId || (isStoredLeagueListed ? String(savedMiniLeagueId) : '');

  const chosenMiniLeagueId = showLeagueFallback
    ? toPositiveId(manualLeagueInput)
    : toPositiveId(selectValue);

  const canSubmit =
    isChangeOpen &&
    effectiveFplTeamId !== null &&
    chosenMiniLeagueId !== null &&
    isLookupCurrentForInput &&
    !!accessToken &&
    !mutation.isLoading;

  const handleStartEditing = () => {
    mutation.reset();
    setSelectedLeagueId('');
    setManualLeagueInput('');
    // The saved FPL ID seeds the input and the lookup, so the stored league
    // can be listed and pre-selected for the member to change either value.
    setFplIdInput(String(savedFplTeamId));
    setLookupFplId(savedFplTeamId);
    setIsEditing(true);
  };

  const handleSave = () => {
    // canSubmit already gates the button on these; re-checked so a stale
    // click can never pair a drifted FPL ID with an old league choice, and a
    // save started after the deadline passed mid-edit is dropped rather than
    // written.
    if (
      !isChangeOpen ||
      effectiveFplTeamId === null ||
      chosenMiniLeagueId === null ||
      !isLookupCurrentForInput
    ) {
      return;
    }

    // The PUT is full-replace on both fields, so a corrected FPL ID and/or
    // mini league overwrites the saved values.
    mutation.mutate({
      fplTeamId: effectiveFplTeamId,
      miniLeagueId: chosenMiniLeagueId
    });
  };

  return (
    <section
      className="mt-10 mb-10 border-t border-neutral-200 pt-10 md:mb-0"
      data-cy="account-mini-league-section"
    >
      <H1>Your mini league</H1>
      <p className="mt-2 max-w-[620px] font-open-sans text-sm leading-5 text-neutral-600">
        {isChangeOpen
          ? `The mini league we'll hold you to for the Mini-League Promise. You can change your league or FPL ID until the Gameweek 3 deadline${
              deadlineDisplay ? ` (${deadlineDisplay})` : ''
            }.`
          : "The mini league we'll hold you to for the Mini-League Promise."}
      </p>

      {isEditing ? (
        <div className="mt-5 flex max-w-[520px] flex-col gap-4">
          <div>
            <label
              htmlFor="account-mini-league-fpl-id"
              className={LABEL_CLASSES}
            >
              Your FPL ID
            </label>
            <input
              id="account-mini-league-fpl-id"
              data-cy="account-mini-league-fpl-id"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 1234567"
              className={INPUT_CLASSES}
              value={fplIdInput}
              onChange={(event) =>
                setFplIdInput(event.target.value.replace(/\D/g, ''))
              }
            />
          </div>

          <Button
            variant="secondary"
            onClick={() =>
              effectiveFplTeamId !== null && setLookupFplId(effectiveFplTeamId)
            }
            disabled={!canLookUpLeagues}
            dataCy="account-mini-league-find"
          >
            Find my mini leagues
          </Button>

          {lookupQuery.isFetching && (
            <p className="animate-pulse font-open-sans text-sm text-neutral-600">
              Looking up your mini leagues…
            </p>
          )}

          {!lookupQuery.isFetching &&
            isLookupCurrentForInput &&
            editLeagues.length > 0 && (
              <div>
                <label
                  htmlFor="account-mini-league-select"
                  className={LABEL_CLASSES}
                >
                  Choose your mini league
                </label>
                <select
                  id="account-mini-league-select"
                  data-cy="account-mini-league-select"
                  className={INPUT_CLASSES}
                  value={selectValue}
                  onChange={(event) => setSelectedLeagueId(event.target.value)}
                >
                  <option value="" disabled>
                    Select a mini league
                  </option>
                  {editLeagues.map((league) => (
                    <option key={league.leagueId} value={league.leagueId}>
                      {`${league.name} (${league.leagueId})`}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {!lookupQuery.isFetching &&
            isLookupCurrentForInput &&
            showLeagueFallback && (
              <div>
                <label
                  htmlFor="account-mini-league-manual"
                  className={LABEL_CLASSES}
                >
                  Your mini league ID
                </label>
                <input
                  id="account-mini-league-manual"
                  data-cy="account-mini-league-manual"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="e.g. 987654"
                  className={INPUT_CLASSES}
                  value={manualLeagueInput}
                  onChange={(event) =>
                    setManualLeagueInput(event.target.value.replace(/\D/g, ''))
                  }
                />
                <p className="mt-1 font-open-sans text-xs text-neutral-500">
                  We couldn&rsquo;t list your leagues yet. Paste the ID from
                  your league&rsquo;s URL on the FPL site instead.
                </p>
              </div>
            )}

          {!isLookupCurrentForInput && effectiveFplTeamId !== null && (
            <p className="font-open-sans text-sm text-amber-600">
              You changed your FPL ID. Search again to update your mini leagues.
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!canSubmit}
              dataCy="account-mini-league-save"
            >
              {mutation.isLoading ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              variant="tertiary"
              onClick={() => setIsEditing(false)}
              dataCy="account-mini-league-cancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-5">
          <div className="grid max-w-[900px] grid-cols-2 gap-2 p-1 md:grid-cols-4">
            <div className="flex flex-col">
              <H4 className="pb-1">League</H4>
              <BodyRegular data-cy="account-mini-league-name">
                {storedLeagueName ?? `Mini league #${savedMiniLeagueId}`}
              </BodyRegular>
            </div>
            {storedLeagueName !== null && (
              <div className="flex flex-col">
                <H4 className="pb-1">League ID</H4>
                <BodyRegular data-cy="account-mini-league-id">
                  {savedMiniLeagueId}
                </BodyRegular>
              </div>
            )}
            {savedFplTeamId !== null && (
              <div className="flex flex-col">
                <H4 className="pb-1">FPL ID</H4>
                <BodyRegular>{savedFplTeamId}</BodyRegular>
              </div>
            )}
            {submittedOn !== null && (
              <div className="flex flex-col">
                <H4 className="pb-1">Submitted</H4>
                <BodyRegular>{submittedOn}</BodyRegular>
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-4">
            {/* A change needs a stored FPL ID to seed the form; a profile
                without one gets no entry into a form it could never
                pre-fill. */}
            {isChangeOpen && savedFplTeamId !== null && (
              <Button
                variant="secondary"
                onClick={handleStartEditing}
                dataCy="account-mini-league-change"
              >
                Change details
              </Button>
            )}
            {!isChangeOpen && (
              <p
                data-cy="account-mini-league-locked"
                className="font-open-sans text-sm text-neutral-600"
              >
                Locked for the 2026/27 season. League changes closed at the
                Gameweek 3 deadline.
              </p>
            )}
            {mutation.isSuccess && (
              <p className="font-open-sans text-sm font-bold text-success-700">
                Mini league updated.
              </p>
            )}
            {mutation.isError && (
              <p className="font-open-sans text-sm font-bold text-danger-600">
                Something went wrong saving your mini league. Please try again.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default MiniLeagueSection;
